<template>
  <v-dialog v-model="dialog" width="750" height="400" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card v-if="statusLabel">
      <v-card-title class="headline orange lighten-3 d-flex flex-row">
        <v-layout align-center>
          <v-chip>{{statusLabel}}</v-chip>
          <v-flex grow>{{issue.showStreetAddress}}</v-flex>
          <v-spacer />
          <v-chip v-if="issue.status < 9" @click="confirm('Close the case', '', closeCase)">
            <v-icon color="secondary">done_outline</v-icon>Complete
          </v-chip>
          <v-chip v-else @click="confirm('Reopen the case', '', reopenCase)">
            <v-icon color="secondary">keyboard_return</v-icon>Reopen
          </v-chip>
          <v-btn flat icon class="align--right" @click.stop="accept('OK')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>

      <v-card-title>
        <v-flex style="padding: 0 30px;">
          <v-textarea
            label="Description"
            auto-grow
            outlined
            rows="2"
            row-height="15"
            v-model="description"
            append-icon="save"
            @click:append="saveDescription"
          >
          </v-textarea>
        </v-flex>
        <v-flex class="d-flex flex-row justify-end">
          <v-menu
            :nudge-right="4"
            lazy
            transition="scale-transition"
            offset-y
            :close-on-content-click="true"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="issue.timestamp | moment('D/MM/YYYY')"
                label="Due Date"
                readonly
                append-icon="event"
                v-on="on"
                style="width: 70px;"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="due_date"
              no-title
              ref="date_picker"
            ></v-date-picker>
          </v-menu>
        </v-flex>

      </v-card-title>

      <v-card-title>
        <v-layout row class="mb-2" style="padding: 0 36px;">
          <v-flex xs5>
            <h2 class="title">
              <v-icon color="blue">house</v-icon>Owner
            </h2>
            <v-divider class="my-2 primary" />
            <v-list dense>
              <v-chip>
                <v-icon color="primary">person</v-icon>{{issue.house.owner_firstname + ' ' + issue.house.owner_lastname}}
              </v-chip>
              <v-chip>
                <v-icon color="primary">phone</v-icon>{{issue.house.owner_phone}}
              </v-chip>
              <v-chip>
                <v-icon color="primary">email</v-icon>{{issue.house.owner_email}}
              </v-chip>
              <v-divider class="my-1 primary" />
              <v-chip :class="{ disabled: issue.status !== 0 }" @click.stop="issue.status === 0 && confirm('Sending notification to owner', '', emailNotification)">
                <v-icon color="red">email</v-icon>Email notification
              </v-chip>
              <v-chip :class="{ disabled: issue.status !== 1, selected: owner_fix }" @click.stop="issue.status === 1 && confirm('Owner\'s response', 'Owner says he(she) will fix it alone', () => owner_fix = true)">
                <v-icon color="primary">attribution</v-icon>Owner will fix it
                <v-icon color="primary" right v-if="owner_fix">task_alt</v-icon>
              </v-chip>
              <v-chip :class="{ disabled: issue.status !== 1, selected: !owner_fix }" @click.stop="issue.status === 1 && confirm('Owner\'s response', 'Owner asks us to fix it', () => owner_fix = false)">
                <v-icon color="primary">handyman</v-icon>Asks us to fix it
                <v-icon color="red" right v-if="!owner_fix">task_alt</v-icon>
              </v-chip>
            </v-list>
          </v-flex>
          <v-flex xs5>
            <h2 class="title">
              <v-icon color="green">person</v-icon>Tenant
            </h2>
            <v-divider class="my-2 green" />
            <v-list dense>
              <v-chip>
                <v-icon color="green">person</v-icon>{{tenant.tenant_name}}
              </v-chip>
              <v-chip>
                <v-icon color="green">phone</v-icon>{{tenant.tenant_phone}}
              </v-chip>
              <v-chip>
                <v-icon color="green">email</v-icon>{{tenant.tenant_email}}
              </v-chip>
              <v-divider class="my-1 primary" />
              <v-chip class="ma-2" :class="{ selected: tenantAdmitFixed }" @click.stop="issue.status === 1 && confirm('To finish the fix', 'Tenant admits the issue fixed', tenantAdmitFix)">
                <v-icon color="green">thumb_up</v-icon>It is fixed
              </v-chip>
              <v-chip @click.stop="confirm('Tenant\'s feedback', 'Tenant says the issue is still there')">
                <v-icon color="green">sentiment_very_dissatisfied</v-icon>Not fixed yet
              </v-chip>
              <v-chip @click.stop="confirm('Tenant\'s feedback', 'Tenant says: ...')">
                <v-icon color="green">mood_bad</v-icon>Other comments
              </v-chip>
            </v-list>
          </v-flex>
          <v-flex xs5>
            <h2 class="title">
              <v-layout>
                <v-icon color="secondary">handyman</v-icon>Handyman
              </v-layout>
            </h2>
            <v-divider class="my-2 secondary" />
            <v-list dense v-if="!owner_fix">
              <v-layout style="height: 40px;">
                <v-icon color="secondary">person</v-icon>
                <v-select item-text="text" key="text" item-value="value"
                  :items="handymenItems"
                  label="Choose an handyman"
                  v-model="selectedHandyman"
                  solo flat hide-details outlined
                ></v-select>
              </v-layout>
              <template v-if="selectedHandyman">
                <v-chip>
                  <v-icon color="secondary">phone</v-icon>{{selectedHandyman.phone}}
                </v-chip>
                <v-chip>
                  <v-icon color="secondary">email</v-icon>{{selectedHandyman.email}}
                </v-chip>
                <v-divider class="my-1 primary" />
                <v-chip :class="{ disabled: issue.status >= 8, selected: handymanConfirm === 'billOnOwner' }" @click.stop="issue.status < 9 && confirm('Handyman confirms', 'Bill on owner\'s', billOnOwner)">
                  <v-icon color="secondary">house</v-icon>Bill on owner's
                </v-chip>
                <v-chip :class="{ disabled: issue.status >= 8, selected: handymanConfirm === 'billOnTenant' }" @click.stop="issue.status < 9 && confirm('Handyman confirms', 'Bill on tenant\'s', billOnTenant)">
                  <v-icon color="secondary">person</v-icon>Bill on tenant's
                </v-chip>
                <v-chip :class="{ disabled: issue.status >= 8, selected: handymanConfirm === 'handymanRefuses' }" @click.stop="issue.status < 9 && confirm('Handyman refuses', 'Handyman explains that...', handymanRefuses)">
                  <v-icon color="secondary">sentiment_very_dissatisfied</v-icon>Cannot fix
                </v-chip>
              </template>
            </v-list>
          </v-flex>
        </v-layout>
      </v-card-title>

      <v-card-text style="padding: 0 20px;" class="container scroll">
        <v-divider class="my-1 primary" />
        <v-flex v-if="issue">
          <v-list v-for="note in issue.note" :key="note.id" class="issue">
            <v-chip style="width: 240px">
              <v-icon color='red'>notes</v-icon>
              {{note.account.account}}@{{note.created_at}}
            </v-chip>
            <v-chip style="width: 400px">
              <span>{{note.message}}</span>
            </v-chip>
          </v-list>
        </v-flex>
      </v-card-text>
      <v-divider />
      <v-card-actions class="status-action-group">
      </v-card-actions>
    </v-card>
    <ConfirmDialog ref="dlg"/>
  </v-dialog>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import issueStatus from '@/helpers/issue_status';
import ConfirmDialog from '@/components/ConfirmDialog';
import ownerNotification from '@/template/issue/ownerNotification';

export default {
  name: 'IssueDialog',
  components: {
    ConfirmDialog,
  },
  data: () => ({
      dialog: false,
      busy: false,
      newNote: '',
      description: '',
      due_date: null,
      statusMap: [{
        status: issueStatus.Proposed,
        label: 'Proposed',
      }, {
        status: issueStatus.Assigned,
        label: 'Assigned',
      }, {
        status: issueStatus.Performed,
        label: 'Performed',
      }, {
        status: issueStatus.Closed,
        label: 'Closed',
      }]
  }),

  watch: {
    due_date(val, oldValue) {
      this.update({ id: this.issue.id, timestamp: moment(val).unix()})
    },
    dialog(val, oldValue) {
      if (val) {
        this.description = this.issue.description;
      }
    },
  },
  computed: {
    ...mapGetters({
      issue: 'Issues/getSelected',
      handymen: 'Issues/getHandymen',
      account: 'User/getUser',
    }),
    handymenItems() {
      return this.handymen.map(item => ({
        text: item.account,
        value: item,
      }))
    },
    tenant() {
      const tenants = this.issue.tenant;
      if (tenants && tenants.length > 0) {
        return tenants[0];
      }
      return {
        tenant_name: 'Unknown',
        tenant_email: 'Not registered',
        tenant_phone: 'Not registered',
      }
    },
    leftDays() {
      const now = moment();
      const leaseCheckoutDate = moment(this.issue.timestamp, 'YYYY-MM-DD');
      const days = leaseCheckoutDate.diff(now, 'days')
      return days;
    },
    statusLabel() {
      const map = this.statusMap.find(map => map.status === this.issue.status);
      return map? map.label: '';
    },
    owner_fix: {
      get() {
        const result = JSON.parse(this.issue.result) || { owner_fix: false }
        return result.owner_fix
      } ,
      set(value) {
        const result = JSON.parse(this.issue.result) || { owner_fix: false };
        result.owner_fix = value;
        this.update({ id: this.issue.id, result: JSON.stringify(result) })
      }
    },
    selectedHandyman: {
      get() {
        const result = JSON.parse(this.issue.result) || { selectedHandyman: {} }
        return result.selectedHandyman
      } ,
      set(value) {
        if (this.issue.status >= issueStatus.Performed) {
          return;
        }
        const result = JSON.parse(this.issue.result) || { selectedHandyman: {} };
        result.selectedHandyman = value;
        this.update({ id: this.issue.id, result: JSON.stringify(result) })
      }
    },
    tenantAdmitFixed: {
      get() {
        const result = JSON.parse(this.issue.result) || { tenantAdmitFixed: false }
        return result.tenantAdmitFixed
      } ,
      set(value) {
        const result = JSON.parse(this.issue.result) || { tenantAdmitFixed: {} };
        result.tenantAdmitFixed = value;
        this.update({ id: this.issue.id, result: JSON.stringify(result) })
      }
    },
    handymanConfirm: {
      get() {
        const result = JSON.parse(this.issue.result) || { handymanConfirm: false }
        return result.handymanConfirm
      } ,
      set(value) {
        const result = JSON.parse(this.issue.result) || { handymanConfirm: false };
        result.handymanConfirm = value;
        this.update({ id: this.issue.id, result: JSON.stringify(result) })
      }
    },
  },
  methods: {
    ...mapActions({
      assign: 'Issues/assign',
      cancel: 'Issues/cancel',
      finish: 'Issues/finish',
      close: 'Issues/close',
      reopen: 'Issues/reopen',
      comment: 'Issues/comment',
      update: 'Issues/update',
      uploadInvoice: 'Issues/uploadInvoice',
      fetchHandymen: 'Issues/fetchHandymen',
      sendNotification: 'LeaseList/sendNotification',
    }),
    async emailNotification() {
      this.busy = true;
      console.log({issue: this.issue })
      const email = { to: this.issue.house.owner_email, subject: ownerNotification.subject(this.issue), content: ownerNotification.body(this.issue)};
      await this.sendNotification(email);
      this.busy = false;
      this.assign(this.issue);
    },
    async upload() {
      this.$refs.upload.click()
    },
    async finishUpload(event) {
      if (event.target.files.length > 0) {
        const file = event.target.files[0];
        console.log('finishUpload', { target: event.target, file })
        await this.uploadInvoice({issue: this.issue, file})
      }
    },
    async reopenCase() {
      this.reopen(this.issue)
    },
    async closeCase() {
      this.close(this.issue)
    },
    async saveDescription() {
      this.update({ id: this.issue.id, description: this.description})
    },
    async tenantAdmitFix() {
      this.tenantAdmitFixed = true;
      this.finish(this.issue);
    },
    async billOnOwner() {
      this.handymanConfirm = 'billOnOwner'
    },
    async billOnTenant() {
      this.handymanConfirm = 'billOnTenant'
    },
    async handymanRefuses() {
      this.handymanConfirm = 'handymanRefuses'
    },
    async confirm(title, newNote, actionConfirmed) {
      try {
        newNote = await this.$refs.dlg.open(title, newNote)
        await this.comment({issue: this.issue, note: newNote});
        actionConfirmed(newNote)
      }
      catch(reason) {
      }
    },
    open() {
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
        this.dialog = true;
      });
    },
    accept(str) {
      this.dialog = false;
      this.resolve(str);
    },
    cancel(str) {
      this.dialog = false;
      this.reject(str);
    }
  },
  async mounted() {
    console.log('mounted', {issue: this.issue, handymen: this.handymen})
    await this.fetchHandymen();
  }
}
</script>

<style scoped>
  .avatar {
    padding-right: 0.5rem;
  }
  .status-action-group {
    display: flex;
    flex-direction: line;
    justify-content: center;
    align-items: center;
  }
  .status-action {
    width: 400px;
    padding: 1rem;
    margin: 1rem;
  }
  .contact {
    display: flex;
    flex-direction: row;
  }
  .contact:hover {
    background-color : lightgray;
    border-radius: 5px;
  }
  .issue {
    padding: 0;
  }
  .issue:hover {
    background-color: burlywood;
    border-radius: 5px;
  }
  .scroll {
    overflow-y: scroll
  }
  .v-card {
    display: flex !important;
    flex-direction: column;
  }

  .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
  .v-card__title {
    padding: 8px 16px;
  }
  .selected {
    background-color: lightseagreen;
  }
  .disabled {
    color: grey;
  }
</style>